import React from 'react';
import { useTheme } from 'contexts/ThemeContext';

interface SpinnerProps {
  size?: 'small' | 'medium' | 'large';
  color?: 'primary' | 'white' | 'gray';
  text?: string;
  fullPage?: boolean;
  className?: string;
}

const Spinner: React.FC<SpinnerProps> = ({
  size = 'medium',
  color = 'primary',
  text,
  fullPage = false,
  className = '',
}) => {
  const { isDark } = useTheme();

  const getSize = () => {
    switch (size) {
      case 'small':
        return 16;
      case 'large':
        return 32;
      default:
        return 24;
    }
  };

  const getBorderWidth = () => {
    switch (size) {
      case 'small':
        return 2;
      case 'large':
        return 3;
      default:
        return 2;
    }
  };

  const getTextColor = () => {
    switch (color) {
      case 'white':
        return '#FFFFFF';
      case 'gray':
        return isDark ? '#90A1B9' : '#64748B';
      default:
        return isDark ? '#E2E8F0' : '#314158';
    }
  };

  const px = getSize();
  const borderW = getBorderWidth();
  const isGradient = color === 'primary';

  const spinStyle = { animation: 'spin 0.6s linear infinite' };

  const spinnerElement = isGradient ? (
    // Gradient spinner using conic-gradient
    <div
      className="rounded-full"
      style={{
        ...spinStyle,
        width: px,
        height: px,
        background: `conic-gradient(from 180deg, transparent 60%, #2B7FFF 80%, #AD46FF 100%)`,
        mask: `radial-gradient(farthest-side, transparent calc(100% - ${borderW}px), #000 calc(100% - ${borderW}px))`,
        WebkitMask: `radial-gradient(farthest-side, transparent calc(100% - ${borderW}px), #000 calc(100% - ${borderW}px))`,
      }}
    />
  ) : (
    // Solid color spinner
    <div
      className="rounded-full"
      style={{
        ...spinStyle,
        width: px,
        height: px,
        border: `${borderW}px solid transparent`,
        borderTopColor: color === 'white' ? '#FFFFFF' : '#64748B',
      }}
    />
  );

  const spinnerContent = (
    // role="status" + aria-live makes the loading state announced by screen
    // readers, and gives tests a stable handle that does not depend on DOM
    // structure. aria-label falls back to "Loading" when no visible text.
    <div
      role="status"
      aria-live="polite"
      aria-label={text || 'Loading'}
      className={`flex items-center justify-center ${className}`}
    >
      {spinnerElement}
      {text && (
        <span
          className="text-xs ml-2"
          style={{ color: getTextColor() }}
        >
          {text}
        </span>
      )}
    </div>
  );

  if (fullPage) {
    return (
      <div
        className="flex items-center justify-center w-full flex-1 min-h-screen"
        style={{ background: isDark ? '#0F172B' : '#F8FAFC' }}
      >
        {spinnerContent}
      </div>
    );
  }

  return spinnerContent;
};

export default Spinner;
